/**
 * Visibility Trigger Component
 * Extends existing theme patterns to provide reusable hover/click visibility triggers
 * Compatible with existing cart visibility and data attribute patterns
 */

class VisibilityTriggerManager {
  constructor() {
    this.triggers = new Map();
    this.activeTriggers = new Set();
    this.visibilityDependencies = new Map();
    this.rafId = null;
    this.popoverObserver = null;
    this.observedPopovers = new WeakSet();
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupTriggers());
    } else {
      this.setupTriggers();
    }

    // Re-initialize when new content is added (for dynamic content)
    this.observeMutations();
    
    // Start continuous visibility dependency monitoring
    this.startVisibilityMonitoring();
    
    // Listen for standardized URL change events
    document.addEventListener('url-change', () => {
      this.checkAllVisibilityDependencies();
    });
    window.addEventListener('url-change', () => {
      this.checkAllVisibilityDependencies();
    });
    
    // Listen for browser navigation (back/forward)
    window.addEventListener('popstate', () => {
      // Dispatch standardized event for browser navigation
      if (window.theme && window.theme.utils && window.theme.utils.dispatchUrlChange) {
        window.theme.utils.dispatchUrlChange('browser-navigation', {
          action: 'popstate'
        });
      }
      this.checkAllVisibilityDependencies();
    });
    
    // Intercept pushState/replaceState to dispatch standardized events
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = function(...args) {
      originalPushState.apply(history, args);
      // Dispatch standardized event
      if (window.theme && window.theme.utils && window.theme.utils.dispatchUrlChange) {
        window.theme.utils.dispatchUrlChange('history-push', {
          state: args[0],
          url: args[2]
        });
      }
      // Also dispatch legacy event for backward compatibility
      window.dispatchEvent(new Event('pushstate'));
    };
    
    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args);
      // Dispatch standardized event
      if (window.theme && window.theme.utils && window.theme.utils.dispatchUrlChange) {
        window.theme.utils.dispatchUrlChange('history-replace', {
          state: args[0],
          url: args[2]
        });
      }
      // Also dispatch legacy event for backward compatibility
      window.dispatchEvent(new Event('replacestate'));
    };
    
    // Also listen for legacy pushstate/replacestate events for backward compatibility
    window.addEventListener('pushstate', () => this.checkAllVisibilityDependencies());
    window.addEventListener('replacestate', () => this.checkAllVisibilityDependencies());
    
    // Listen for popover toggle events to re-initialize triggers when popovers open
    // This is critical for modals/popovers that contain visibility triggers
    document.addEventListener('toggle', (event) => {
      const target = event.target;
      // Check if this is a popover opening event
      if (target && target.hasAttribute && target.hasAttribute('popover')) {
        const newState = event.newState || (target.matches(':popover-open') ? 'open' : 'closed');
        if (newState === 'open') {
          // Small delay to ensure DOM is fully updated
          setTimeout(() => {
            this.setupTriggers();
          }, 0);
        }
      }
    });
    
    // Set up popover observer for state changes
    this.setupPopoverObserver();
    
    // Also watch for new popovers being added to the DOM
    setTimeout(() => {
      this.observeAllPopovers();
    }, 100);
  }

  setupTriggers() {
    const triggerElements = document.querySelectorAll('[data-visibility-trigger]');
    
    let newCount = 0;
    triggerElements.forEach(element => {
      if (!this.triggers.has(element)) {
        this.registerTrigger(element);
        newCount++;
      }
    });

    // Always re-initialize defaults when setupTriggers is called
    // This ensures correct state when content is dynamically added (e.g., modals)
    // After registering all triggers, hide ALL visibility targets by default
    // BUT skip those that have data-visibility-default (they will be handled by initializeDefaults)
    this.hideAllTargetsByDefault();
    
    // Initialize default active triggers - this ensures only defaults are visible
    // This must run every time to handle dynamically added content
    this.initializeDefaults();
  }

  hideAllTargetsByDefault() {
    // Hide all potential target elements by ID
    // BUT skip targets that are associated with default-active triggers OR hide-on-interact triggers
    Array.from(this.triggers.values()).forEach(trigger => {
      // Only hide if this trigger is NOT default active AND NOT hide-on-interact
      // hide-on-interact targets should remain visible by default
      if (!trigger.defaultActive && !trigger.hideOnInteract) {
        const target = document.getElementById(trigger.triggerId);
        if (target) {
          target.classList.add('hidden');
          target.style.display = 'none'; // Explicitly set display:none to override any CSS
        }
        // Remove .active class from trigger element
        trigger.element.classList.remove('active');
      }
    });
  }

  initializeDefaults() {
    // First, hide ALL triggers in groups that have default triggers
    // Group ALL triggers by their group ID (not just defaults)
    const allGroups = new Map();
    Array.from(this.triggers.values()).forEach(trigger => {
      const groupId = trigger.groupId || null;
      if (groupId) {
        if (!allGroups.has(groupId)) {
          allGroups.set(groupId, []);
        }
        allGroups.get(groupId).push(trigger);
      }
    });
    
    // Find all default active triggers
    const defaultTriggers = Array.from(this.triggers.values()).filter(trigger => trigger.defaultActive);
    
    // Group default triggers by their group ID
    const groupedDefaults = new Map();
    if (defaultTriggers.length > 0) {
      defaultTriggers.forEach(trigger => {
        const groupId = trigger.groupId || 'default';
        if (!groupedDefaults.has(groupId)) {
          groupedDefaults.set(groupId, []);
        }
        groupedDefaults.get(groupId).push(trigger);
      });
    }
    
    // For each group with defaults, hide all non-default triggers first
    groupedDefaults.forEach((defaultTriggersInGroup, groupId) => {
      const allTriggersInGroup = allGroups.get(groupId) || [];
      
      // Hide ALL targets in this group first (including defaults)
      // BUT skip hide-on-interact triggers (they should remain visible)
      allTriggersInGroup.forEach(trigger => {
        // Skip hide-on-interact triggers - they should remain visible by default
        if (trigger.hideOnInteract) {
          return;
        }
        
        const target = document.getElementById(trigger.triggerId);
        if (target) {
          target.classList.add('hidden');
          target.style.display = 'none'; // Explicitly set display:none to override any CSS
          trigger.isActive = false;
          this.activeTriggers.delete(trigger.triggerId);
        }
        // Remove .active class from all trigger elements in the group
        trigger.element.classList.remove('active');
      });
      
      // Now show ONLY the first default trigger
      if (defaultTriggersInGroup.length > 0) {
        const firstDefaultTrigger = defaultTriggersInGroup[0];
        this.showTarget(firstDefaultTrigger);
      }
    });
    
    // For groups WITHOUT any default triggers, ensure all targets are hidden
    // BUT skip hide-on-interact triggers (they should remain visible)
    allGroups.forEach((triggers, groupId) => {
      if (!groupedDefaults.has(groupId)) {
        // This group has no defaults, so hide all targets
        // BUT skip hide-on-interact triggers
        triggers.forEach(trigger => {
          // Skip hide-on-interact triggers - they should remain visible by default
          if (trigger.hideOnInteract) {
            return;
          }
          
          const target = document.getElementById(trigger.triggerId);
          if (target) {
            target.classList.add('hidden');
            target.style.display = 'none'; // Explicitly set display:none to override any CSS
            trigger.isActive = false;
            this.activeTriggers.delete(trigger.triggerId);
          }
          // Remove .active class from trigger elements
          trigger.element.classList.remove('active');
        });
      }
    });
  }

  registerTrigger(element) {
    const triggerId = element.getAttribute('data-visibility-trigger');
    const triggerType = element.getAttribute('data-visibility-type') || 'hover';
    const groupId = element.getAttribute('data-visibility-group');
    const delay = 0;
    const sticky = element.hasAttribute('data-visibility-sticky');
    const defaultActive = element.hasAttribute('data-visibility-default');
    const hideOnInteract = element.hasAttribute('data-visibility-hide-on-interact');

    const triggerData = {
      element,
      triggerId,
      triggerType,
      groupId,
      delay,
      sticky,
      defaultActive,
      hideOnInteract,
      timeout: null,
      isActive: false
    };

    this.triggers.set(element, triggerData);

    // Ensure target is visible by default when hide-on-interact is enabled
    if (hideOnInteract) {
      const target = document.getElementById(triggerId);
      if (target) {
        target.classList.remove('hidden');
        target.style.display = '';
      }
    }

    // Set up event listeners based on trigger type
    if (triggerType === 'hover') {
      this.setupHoverTrigger(triggerData);
    } else if (triggerType === 'click') {
      this.setupClickTrigger(triggerData);
    } else if (triggerType === 'focus') {
      this.setupFocusTrigger(triggerData);
    }
  }

  setupHoverTrigger(triggerData) {
    const { element, delay, sticky, triggerId, groupId, defaultActive, hideOnInteract } = triggerData;

    element.addEventListener('mouseenter', () => {
      this.clearTimeout(triggerData);
      if (hideOnInteract) {
        // Hide target on hover when hide-on-interact is enabled
        if (delay > 0) {
          triggerData.timeout = setTimeout(() => {
            this.hideTarget(triggerData);
          }, delay);
        } else {
          this.hideTarget(triggerData);
        }
      } else {
        // Normal behavior: show target on hover
      if (delay > 0) {
        triggerData.timeout = setTimeout(() => {
          this.showTarget(triggerData);
        }, delay);
      } else {
        this.showTarget(triggerData);
        }
      }
    });

    element.addEventListener('mouseleave', () => {
      this.clearTimeout(triggerData);
      
      if (hideOnInteract) {
        // Show target again when mouse leaves (restore to visible state)
        if (delay > 0) {
          triggerData.timeout = setTimeout(() => {
            this.showTarget(triggerData);
          }, delay);
        } else {
          this.showTarget(triggerData);
        }
      } else {
        // Normal behavior: hide target on mouseleave (unless sticky/defaultActive)
      // Don't hide if:
      // 1. sticky is true (stays visible on mouseleave)
      // 2. defaultActive is true (stays visible until another group member is hovered)
      const shouldStayVisible = sticky || defaultActive;
      
      if (!shouldStayVisible) {
        if (delay > 0) {
          triggerData.timeout = setTimeout(() => {
            this.hideTarget(triggerData);
          }, delay);
        } else {
          this.hideTarget(triggerData);
          }
        }
      }
    });
  }

  setupClickTrigger(triggerData) {
    const { element, sticky, hideOnInteract } = triggerData;

    element.addEventListener('click', (e) => {
      e.preventDefault();
      
      if (hideOnInteract) {
        // Hide target on click when hide-on-interact is enabled
        // Toggle: if already hidden, show it; if visible, hide it
        const target = document.getElementById(triggerData.triggerId);
        if (target && target.classList.contains('hidden')) {
          // Target is hidden, show it
          this.showTarget(triggerData);
        } else {
          // Target is visible, hide it
          this.hideTarget(triggerData);
        }
      } else {
        // Normal behavior: toggle target visibility
      // If sticky is enabled and trigger is already active, don't hide it
      if (sticky && triggerData.isActive) {
        return;
      }
      
      this.toggleTarget(triggerData);
      }
    });

    // Close on outside click (but respect sticky setting and hide-on-interact)
    if (!hideOnInteract) {
    document.addEventListener('click', (e) => {
      if (triggerData.isActive && !element.contains(e.target)) {
        // Only hide on outside click if not sticky
        if (!sticky) {
          this.hideTarget(triggerData);
        }
      }
    });
    }
  }

  setupFocusTrigger(triggerData) {
    const { element } = triggerData;

    element.addEventListener('focus', () => {
      this.showTarget(triggerData);
    });

    element.addEventListener('blur', () => {
      // Small delay to allow for focus to move to target content
      setTimeout(() => {
        if (!element.contains(document.activeElement)) {
          this.hideTarget(triggerData);
        }
      }, 100);
    });
  }

  showTarget(triggerData) {
    const { triggerId, groupId, element } = triggerData;

    // Hide other targets in the same group (including sticky ones)
    if (groupId) {
      this.hideGroupTargets(groupId, triggerId);
    }

    // Show specific target
    this.showTargetElements(triggerId);

    // Hide elements that should be hidden when any trigger is active
    this.hideElementsOnTrigger();

    // Update state
    triggerData.isActive = true;
    this.activeTriggers.add(triggerId);

    // Add .active class to trigger element for styling
    element.classList.add('active');

    // Dispatch custom event
    element.dispatchEvent(new CustomEvent('visibility:show', {
      detail: { triggerId, groupId },
      bubbles: true
    }));
  }

  hideTarget(triggerData) {
    const { triggerId, element } = triggerData;

    // Hide specific target
    this.hideTargetElements(triggerId);

    // Show elements that were hidden
    this.showElementsOnTrigger();

    // Update state
    triggerData.isActive = false;
    this.activeTriggers.delete(triggerId);

    // Remove .active class from trigger element
    element.classList.remove('active');

    // Dispatch custom event
    element.dispatchEvent(new CustomEvent('visibility:hide', {
      detail: { triggerId },
      bubbles: true
    }));
  }

  toggleTarget(triggerData) {
    if (triggerData.isActive) {
      this.hideTarget(triggerData);
    } else {
      this.showTarget(triggerData);
    }
  }

  showTargetElements(triggerId) {
    const target = document.getElementById(triggerId);
    if (target) {
      target.classList.remove('hidden');
      target.style.display = ''; // Remove any inline display:none
    }
  }

  hideTargetElements(triggerId) {
    const target = document.getElementById(triggerId);
    if (target) {
      target.classList.add('hidden');
      target.style.display = 'none'; // Explicitly set display:none to override any CSS
    }
  }

  hideGroupTargets(groupId, excludeTriggerId) {
    // Find all triggers in the same group
    const groupTriggers = Array.from(this.triggers.values()).filter(trigger => 
      trigger.groupId === groupId && trigger.triggerId !== excludeTriggerId
    );
    
    const triggerIds = groupTriggers.map(t => t.triggerId);
    
    // Also find any elements with the group attribute that might not be registered triggers
    // This catches cases where elements have data-visibility-group but aren't trigger elements
    const allGroupElements = document.querySelectorAll(`[data-visibility-group="${groupId}"]`);
    const groupElementIds = Array.from(allGroupElements)
      .map(el => el.id)
      .filter(id => id && id !== excludeTriggerId);
    
    // Hide all targets from registered triggers
    groupTriggers.forEach(triggerData => {
      const target = document.getElementById(triggerData.triggerId);
      
      if (target) {
        target.classList.add('hidden');
        target.style.display = 'none'; // Explicitly set display:none to override any CSS
      }
      
      if (triggerData.isActive) {
        this.hideTarget(triggerData);
      } else {
        triggerData.isActive = false;
        this.activeTriggers.delete(triggerData.triggerId);
        // Remove .active class from trigger element even if not marked as active
        triggerData.element.classList.remove('active');
      }
    });
    
    // Also hide any elements found by group attribute that aren't in triggers list
    groupElementIds.forEach(elementId => {
      if (!triggerIds.includes(elementId)) {
        const target = document.getElementById(elementId);
        if (target) {
          target.classList.add('hidden');
          target.style.display = 'none';
        }
      }
    });
  }

  hideElementsOnTrigger() {
    document.querySelectorAll('[data-visibility-hide-on-trigger]').forEach(element => {
      element.classList.add('hidden');
    });
  }

  showElementsOnTrigger() {
    // Only show if no other triggers are active
    if (this.activeTriggers.size === 0) {
      document.querySelectorAll('[data-visibility-hide-on-trigger]').forEach(element => {
        element.classList.remove('hidden');
      });
    }
  }


  clearTimeout(triggerData) {
    if (triggerData.timeout) {
      clearTimeout(triggerData.timeout);
      triggerData.timeout = null;
    }
  }

  /**
   * Handle visibility dependencies using checkVisibility() API
   * @param {string} triggerId - The ID of the trigger that changed state
   * @param {boolean} isVisible - Whether the trigger is now visible
   */
  startVisibilityMonitoring() {
    // Register all visibility dependencies
    this.registerVisibilityDependencies();
    
    // Start RAF loop for continuous monitoring
    this.monitorVisibilityDependencies();
  }

  registerVisibilityDependencies() {
    const dependentElements = document.querySelectorAll('[data-visibility-show], [data-visibility-hide]');
    
    dependentElements.forEach(element => {
      const showTargets = element.getAttribute('data-visibility-show');
      const hideTargets = element.getAttribute('data-visibility-hide');
      
      // Parse targets into element IDs and URL patterns
      const parseTargets = (targetsString) => {
        if (!targetsString) return { elementIds: [], urlPatterns: [] };
        
        const targets = targetsString.split(',').map(t => t.trim()).filter(t => t);
        const elementIds = [];
        const urlPatterns = [];
        
        targets.forEach(target => {
          // Try to parse as URL pattern first
          // URL patterns are identified by: containing ? or || or = (query params)
          const urlPattern = this.parseUrlPattern(target);
          if (urlPattern) {
            urlPatterns.push(urlPattern);
          } else {
            // If URL pattern parsing fails, treat as element ID
            elementIds.push(target);
          }
        });
        
        return { elementIds, urlPatterns };
      };
      
      const showParsed = parseTargets(showTargets);
      const hideParsed = parseTargets(hideTargets);
      
      const dependency = {
        showTargets: showParsed.elementIds,
        showUrlPatterns: showParsed.urlPatterns,
        hideTargets: hideParsed.elementIds,
        hideUrlPatterns: hideParsed.urlPatterns,
        lastVisibilityState: null
      };
      
      // Initialize with current visibility state
      dependency.lastVisibilityState = this.calculateElementVisibility(element, dependency);
      
      // Apply initial visibility state immediately
      try {
        if (dependency.lastVisibilityState) {
          element.classList.remove('hidden');
        } else {
          element.classList.add('hidden');
        }
      } catch (error) {
        // Silently handle error
      }
      
      this.visibilityDependencies.set(element, dependency);
    });
  }

  monitorVisibilityDependencies() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    
    this.rafId = requestAnimationFrame(() => {
      try {
        this.checkAllVisibilityDependencies();
        this.monitorVisibilityDependencies();
      } catch (error) {
        // Continue monitoring even if there's an error
        this.monitorVisibilityDependencies();
      }
    });
  }

  checkAllVisibilityDependencies() {
    if (this.visibilityDependencies.size === 0) {
      return;
    }
    
    this.visibilityDependencies.forEach((dependency, element) => {
      // Check if element still exists in DOM
      if (!document.contains(element)) {
        this.visibilityDependencies.delete(element);
        return;
      }
      
      const currentVisibility = this.calculateElementVisibility(element, dependency);
      
      if (currentVisibility !== dependency.lastVisibilityState) {
        try {
          if (currentVisibility) {
            element.classList.remove('hidden');
          } else {
            element.classList.add('hidden');
          }
          
          dependency.lastVisibilityState = currentVisibility;
        } catch (error) {
          // Silently handle error
        }
      }
    });
  }

  calculateElementVisibility(element, dependency) {
    const { showTargets, showUrlPatterns, hideTargets, hideUrlPatterns } = dependency;
    
    // If no targets specified, element should be visible by default
    if (showTargets.length === 0 && showUrlPatterns.length === 0 && 
        hideTargets.length === 0 && hideUrlPatterns.length === 0) {
      return true;
    }
    
    // Check if ANY show target (element) is visible
    const anyShowTargetVisible = showTargets.some(targetId => {
      const target = document.getElementById(targetId);
      return target && this.isElementVisible(target);
    });
    
    // Check if ANY show URL pattern matches
    const anyShowUrlPatternMatches = showUrlPatterns.some(pattern => {
      return this.matchesUrlPattern(pattern);
    });
    
    // Check if ANY hide target (element) is visible
    const anyHideTargetVisible = hideTargets.some(targetId => {
      const target = document.getElementById(targetId);
      return target && this.isElementVisible(target);
    });
    
    // Check if ANY hide URL pattern matches
    const anyHideUrlPatternMatches = hideUrlPatterns.some(pattern => {
      return this.matchesUrlPattern(pattern);
    });
    
    // Element should be visible if:
    // - Has show targets: (any show element visible OR any show URL matches) AND (no hide elements visible AND no hide URLs match)
    // - Has only hide targets: (none of hide elements visible AND none of hide URLs match)
    let shouldBeVisible = false;
    
    const hasShowConditions = showTargets.length > 0 || showUrlPatterns.length > 0;
    const hasHideConditions = hideTargets.length > 0 || hideUrlPatterns.length > 0;
    const anyShowMatches = anyShowTargetVisible || anyShowUrlPatternMatches;
    const anyHideMatches = anyHideTargetVisible || anyHideUrlPatternMatches;
    
    if (hasShowConditions) {
      // Has show conditions - element visible if any show condition matches and no hide conditions match
      shouldBeVisible = anyShowMatches && !anyHideMatches;
    } else if (hasHideConditions) {
      // Has only hide conditions - element visible if none of the hide conditions match
      shouldBeVisible = !anyHideMatches;
    }
    
    return shouldBeVisible;
  }

  /**
   * Parse a URL pattern string into a structured object
   * Supports AND logic (using &) and OR logic (using ||)
   * @param {string} pattern - URL pattern string (e.g., "collections/all-men?filter.p.m.custom.category=Boxer+Briefs&filter.p.m.custom.pouchtech=On")
   * @returns {Object|null} - Parsed pattern object or null if invalid
   */
  parseUrlPattern(pattern) {
    if (!pattern || typeof pattern !== 'string') {
      return null;
    }
    
    // Check if pattern contains URL-like structure (query params with = or OR logic with ||)
    const hasQueryParams = pattern.includes('=');
    const hasOrLogic = pattern.includes('||');
    const hasQuestionMark = pattern.includes('?');
    const hasPathSeparator = pattern.includes('/');
    
    // If it doesn't look like a URL pattern, return null (will be treated as element ID)
    // A pattern is a URL if it has:
    // - Query params (=), OR logic (||), question mark (?), OR
    // - Path separator (/) indicating it's a URL path
    if (!hasQueryParams && !hasOrLogic && !hasQuestionMark && !hasPathSeparator) {
      return null;
    }
    
    // Split path and query string
    let path = '';
    let queryString = '';
    
    if (hasQuestionMark) {
      const parts = pattern.split('?');
      path = parts[0] || '';
      queryString = parts.slice(1).join('?'); // Rejoin in case ? appears in values
    } else if (hasQueryParams || hasOrLogic) {
      // No ? but has query params - treat entire string as query params
      queryString = pattern;
    } else {
      // Just a path, no query params
      path = pattern;
    }
    
    // Determine if using OR logic (contains ||) or AND logic (uses &)
    const useOrLogic = queryString.includes('||');
    const separator = useOrLogic ? '||' : '&';
    
    // Parse query parameters
    const queryParams = [];
    if (queryString) {
      const paramPairs = queryString.split(separator).map(p => p.trim()).filter(p => p);
      paramPairs.forEach(pair => {
        // Handle URL-encoded values
        const equalIndex = pair.indexOf('=');
        if (equalIndex > 0) {
          const key = pair.substring(0, equalIndex);
          const value = pair.substring(equalIndex + 1);
          try {
            // Decode URI component (handles %XX encoding)
            let decodedKey = decodeURIComponent(key);
            let decodedValue = decodeURIComponent(value || '');
            
            // Also handle + as space (URL query string encoding)
            // This matches how URLSearchParams.get() decodes values
            decodedValue = decodedValue.replace(/\+/g, ' ');
            
            queryParams.push({ 
              key: decodedKey, 
              value: decodedValue
            });
          } catch (e) {
            // If decoding fails, use raw values but still handle + as space
            const normalizedValue = (value || '').replace(/\+/g, ' ');
            queryParams.push({ key, value: normalizedValue });
          }
        } else if (pair) {
          // Parameter without value
          try {
            queryParams.push({ 
              key: decodeURIComponent(pair), 
              value: '' 
            });
          } catch (e) {
            queryParams.push({ key: pair, value: '' });
          }
        }
      });
    }
    
    // Must have at least one query param to be a valid URL pattern
    if (queryParams.length === 0 && !path) {
      return null;
    }
    
    // Normalize path (remove leading/trailing slashes for storage)
    const normalizedPath = path ? path.replace(/^\/+|\/+$/g, '') : null;
    
    return {
      path: normalizedPath,
      queryParams,
      useOrLogic
    };
  }

  /**
   * Check if the current URL matches a parsed URL pattern
   * @param {Object} pattern - Parsed pattern object from parseUrlPattern()
   * @returns {boolean} - Whether the current URL matches the pattern
   */
  matchesUrlPattern(pattern) {
    if (!pattern) {
      return false;
    }
    
    const currentUrl = new URL(window.location.href);
    const currentPath = currentUrl.pathname;
    const currentParams = new URLSearchParams(currentUrl.search);
    
    // Check path match (if pattern specifies a path)
    if (pattern.path) {
      // Normalize paths (remove leading/trailing slashes for comparison)
      const normalizedCurrentPath = currentPath.replace(/^\/+|\/+$/g, '');
      
      if (pattern.path !== normalizedCurrentPath) {
        return false;
      }
    }
    
    // If no query params specified, just match on path (which was already checked above)
    if (!pattern.queryParams || pattern.queryParams.length === 0) {
      return pattern.path !== null; // Matched if path was specified and matched
    }
    
    // Check query parameter matches
    if (pattern.useOrLogic) {
      // OR logic: at least one query parameter must match
      return pattern.queryParams.some(param => {
        const currentValue = currentParams.get(param.key);
        // Wildcard: match if param exists with any value
        if (param.value === '*') {
          return currentParams.has(param.key);
        }
        // Exact match: param exists and value matches (empty string matches empty value)
        return currentValue !== null && currentValue === param.value;
      });
    } else {
      // AND logic: all query parameters must match (order-independent)
      return pattern.queryParams.every(param => {
        const currentValue = currentParams.get(param.key);
        // Wildcard: match if param exists with any value
        if (param.value === '*') {
          return currentParams.has(param.key);
        }
        // Exact match: param exists and value matches (empty string matches empty value)
        return currentValue !== null && currentValue === param.value;
      });
    }
  }

  /**
   * Check if an element is visible using checkVisibility() API
   * @param {Element} element - The element to check
   * @returns {boolean} - Whether the element is visible
   */
  isElementVisible(element) {
    // Use checkVisibility() if available (modern browsers)
    if (element.checkVisibility) {
      return element.checkVisibility({
        opacityProperty: true,
        visibilityProperty: true,
        contentVisibilityAuto: true
      });
    }
    
    // Fallback for older browsers
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && 
           style.visibility !== 'hidden' && 
           style.opacity !== '0' &&
           element.offsetParent !== null;
  }

  /**
   * Handle show dependency - show this element when ANY target is visible
   * @param {Element} element - The element that should be shown/hidden
   * @param {string} targetIds - Comma-separated list of target IDs to check
   */
  handleShowDependency(element, targetIds) {
    const targets = targetIds.split(',').map(id => id.trim()).filter(id => id);
    
    // Check if ANY target is visible
    const anyTargetVisible = targets.some(targetId => {
      const target = document.getElementById(targetId);
      return target && this.isElementVisible(target);
    });
    
    if (anyTargetVisible) {
      element.classList.remove('hidden');
      element.style.display = '';
    } else {
      element.classList.add('hidden');
      element.style.display = 'none';
    }
  }

  /**
   * Handle hide dependency - hide this element when ANY target is visible
   * @param {Element} element - The element that should be shown/hidden
   * @param {string} targetIds - Comma-separated list of target IDs to check
   */
  handleHideDependency(element, targetIds) {
    const targets = targetIds.split(',').map(id => id.trim()).filter(id => id);
    
    // Check if ANY target is visible
    const anyTargetVisible = targets.some(targetId => {
      const target = document.getElementById(targetId);
      return target && this.isElementVisible(target);
    });
    
    if (anyTargetVisible) {
      element.classList.add('hidden');
      element.style.display = 'none';
    } else {
      element.classList.remove('hidden');
      element.style.display = '';
    }
  }

  setupPopoverObserver() {
    // Also listen for showPopover/hidePopover calls via mutation observer
    // This catches popover state changes that might not fire toggle events
    this.popoverObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          const target = mutation.target;
          if (target.hasAttribute('popover')) {
            const isOpen = target.matches(':popover-open') || target.hasAttribute('open');
            
            // Check if popover just opened (oldValue would be null or empty if it was closed)
            const wasOpen = mutation.oldValue !== null;
            
            // Only trigger if popover just opened (not if it was already open or just closed)
            if (isOpen && !wasOpen) {
              // Small delay to ensure DOM is fully updated and content is loaded
              setTimeout(() => {
                this.setupTriggers();
              }, 50);
            }
          }
        }
      });
    });
  }

  observeAllPopovers() {
    // Observe all existing popover elements for attribute changes
    document.querySelectorAll('[popover]').forEach(popover => {
      if (!this.observedPopovers.has(popover)) {
        this.popoverObserver.observe(popover, {
          attributes: true,
          attributeFilter: ['open'],
          attributeOldValue: true
        });
        this.observedPopovers.add(popover);
      }
    });
  }

  observeMutations() {
    const observer = new MutationObserver((mutations) => {
      let shouldReinitTriggers = false;
      let shouldReinitDependencies = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check for visibility triggers
              if (node.hasAttribute && node.hasAttribute('data-visibility-trigger')) {
                shouldReinitTriggers = true;
              } else if (node.querySelector && node.querySelector('[data-visibility-trigger]')) {
                shouldReinitTriggers = true;
              }
              
              // Check for visibility dependencies
              if (node.hasAttribute && (node.hasAttribute('data-visibility-show') || node.hasAttribute('data-visibility-hide'))) {
                shouldReinitDependencies = true;
              } else if (node.querySelector && node.querySelector('[data-visibility-show], [data-visibility-hide]')) {
                shouldReinitDependencies = true;
              }
              
              // Watch for new popover elements being added
              if (node.hasAttribute && node.hasAttribute('popover')) {
                if (!this.observedPopovers.has(node)) {
                  this.popoverObserver.observe(node, {
                    attributes: true,
                    attributeFilter: ['open'],
                    attributeOldValue: true
                  });
                  this.observedPopovers.add(node);
                }
              } else if (node.querySelector && node.querySelector('[popover]')) {
                // Also check for popovers within the added node
                node.querySelectorAll('[popover]').forEach(popover => {
                  if (!this.observedPopovers.has(popover)) {
                    this.popoverObserver.observe(popover, {
                      attributes: true,
                      attributeFilter: ['open'],
                      attributeOldValue: true
                    });
                    this.observedPopovers.add(popover);
                  }
                });
              }
            }
          });
          
          // Check for removed nodes
          mutation.removedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (this.visibilityDependencies.has(node)) {
                this.visibilityDependencies.delete(node);
              }
            }
          });
        }
      });

      if (shouldReinitTriggers) {
        this.setupTriggers();
      }
      
      if (shouldReinitDependencies) {
        this.registerVisibilityDependencies();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Cleanup method for when the component is destroyed
  destroy() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.triggers.clear();
    this.activeTriggers.clear();
    this.visibilityDependencies.clear();
  }

  // Public API methods
  show(triggerId) {
    const trigger = Array.from(this.triggers.values()).find(t => t.triggerId === triggerId);
    if (trigger) {
      this.showTarget(trigger);
    }
  }

  hide(triggerId) {
    const trigger = Array.from(this.triggers.values()).find(t => t.triggerId === triggerId);
    if (trigger) {
      this.hideTarget(trigger);
    }
  }

  toggle(triggerId) {
    const trigger = Array.from(this.triggers.values()).find(t => t.triggerId === triggerId);
    if (trigger) {
      this.toggleTarget(trigger);
    }
  }

  hideAll() {
    this.activeTriggers.forEach(triggerId => {
      this.hide(triggerId);
    });
  }

  getActiveTriggers() {
    return Array.from(this.activeTriggers);
  }
}

// Initialize the visibility trigger manager
const visibilityTriggerManager = new VisibilityTriggerManager();

// Export for global access
window.VisibilityTriggerManager = visibilityTriggerManager;

// Compatibility with existing theme patterns
window.showVisibilityTarget = (triggerId) => visibilityTriggerManager.show(triggerId);
window.hideVisibilityTarget = (triggerId) => visibilityTriggerManager.hide(triggerId);
window.toggleVisibilityTarget = (triggerId) => visibilityTriggerManager.toggle(triggerId);
